import React, { createContext, useContext, useState, useCallback } from "react";
import { MdClose, MdCheckCircle, MdError, MdWarning, MdInfo } from "react-icons/md";

const ToastContext = createContext(null);

const ICONS = {
  success: <MdCheckCircle size={20} />,
  error: <MdError size={20} />,
  warning: <MdWarning size={20} />,
  info: <MdInfo size={20} />,
};

const COLORS = {
  success: { background: "#d4edda", border: "#c3e6cb", color: "#155724" },
  error: { background: "#f8d7da", border: "#f5c6cb", color: "#721c24" },
  warning: { background: "#fff3cd", border: "#ffeeba", color: "#856404" },
  info: { background: "#d1ecf1", border: "#bee5eb", color: "#0c5460" },
};

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success", duration = 3000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  function dismiss(id) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        style={{
          position: "fixed",
          top: "16px",
          right: "16px",
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          maxWidth: "360px",
          width: "100%",
        }}
      >
        {toasts.map((toast) => {
          const colors = COLORS[toast.type] || COLORS.info;
          return (
            <div
              key={toast.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                borderRadius: "6px",
                border: `1px solid ${colors.border}`,
                background: colors.background,
                color: colors.color,
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                animation: "fadeInDown 0.25s ease",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {ICONS[toast.type]}
                {toast.message}
              </span>
              <button
                onClick={() => dismiss(toast.id)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: colors.color,
                  padding: "0 0 0 8px",
                  lineHeight: 1,
                }}
              >
                <MdClose size={16} />
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
